import * as React from 'react';
import { useSchemes } from '../../schemes/network/schemes.api';
import { Input } from '../../../components/ui/FormField';
import { Button } from '../../../components/ui/Button';

interface SchemeSearchInputProps {
  value: string;
  onChange: (schemeId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function SchemeSearchInput({ value, onChange, disabled, placeholder = 'Search chit scheme...' }: SchemeSearchInputProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { data } = useSchemes({ limit: 200 });
  const schemes = data?.data ?? [];

  const selectedScheme = React.useMemo(() => schemes.find((s) => s.id === value), [schemes, value]);

  const filteredSchemes = React.useMemo(() => {
    if (!searchQuery.trim()) return schemes.slice(0, 50);
    const q = searchQuery.toLowerCase();
    return schemes.filter((s) => s.schemeNumber.toLowerCase().includes(q) || s.name.toLowerCase().includes(q)).slice(0, 50);
  }, [schemes, searchQuery]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayValue = selectedScheme && !isOpen ? `${selectedScheme.schemeNumber} — ${selectedScheme.name}` : searchQuery;

  return (
    <div className="relative" ref={containerRef} style={{ width: '100%' }}>
      <Input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setIsOpen(true);
          if (!e.target.value) onChange('');
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full"
        autoComplete="off"
      />
      {isOpen && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-surface-0 border border-surface-3 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredSchemes.length === 0 ? (
            <div className="px-3 py-2 text-sm text-text-muted">No schemes found</div>
          ) : (
            filteredSchemes.map((scheme) => (
              <Button
                key={scheme.id}
                type="button"
                variant="ghost"
                onClick={() => {
                  onChange(scheme.id);
                  setSearchQuery('');
                  setIsOpen(false);
                }}
                className={`w-full h-auto justify-start rounded-none text-left px-3 py-2 text-sm font-normal ${value === scheme.id ? 'bg-accent-bg' : ''}`}
              >
                <span className="font-mono text-text-secondary mr-2">{scheme.schemeNumber}</span>
                <span className="text-text-primary">{scheme.name}</span>
              </Button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
